import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CoursePlayer.css';
import API_URL from '../api';
import { resolveAssetUrl } from '../utils/assetUrl';
import FlashcardStep from './FlashcardStep';
import HotspotStep from './HotspotStep';
import PdfStep from './PdfStep';
import EmbedStep from './EmbedStep';

const typeIcons = {
    text: '📖',
    video: '🎥',
    pdf: '📄',
    Quiz: '📝',
    DeskTemplate: '🖥️',
    MultiDeskTest: '📚',
    GameScenario: '🎮',
    PizzaTemplate: '🍕',
    ComplexTest: '🧩',
    flashcards: '🃏',
    hotspots: '🎯',
    embed: '🔗'
};

const typeNames = {
    text: 'Текстовий урок',
    video: 'Відео-лекція',
    pdf: 'PDF-матеріал',
    Quiz: 'Практичний квіз',
    DeskTemplate: 'Тест сервірування',
    MultiDeskTest: 'Комбінована сервіровка',
    GameScenario: 'Діалогова гра',
    PizzaTemplate: 'Конструктор піци',
    ComplexTest: 'Комплексний тест',
    flashcards: 'Картки для запам\'ятовування',
    hotspots: 'Активні зони',
    embed: 'Вбудований контент (Academy Ocean)'
};

const CoursePlayer = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [progress, setProgress] = useState(null);
    const [activeStep, setActiveStep] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [testLinkLoading, setTestLinkLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [stepTransition, setStepTransition] = useState('');
    const [showCompletion, setShowCompletion] = useState(true);
    const [activeAttachment, setActiveAttachment] = useState(null);
    const prevAllCompletedRef = useRef(false);

    useEffect(() => {
        fetchCourseProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId]);

    const fetchCourseProgress = async () => {
        try {
            const res = await axios.get(`${API_URL}/courses/student/my`);
            const matched = res.data.find(item => item.course._id === courseId);
            
            if (!matched) {
                setError('Курс не знайдено або не призначено вам.');
                setLoading(false);
                return;
            }
            
            setCourse(matched.course);
            setProgress(matched.progress);
            
            const allSteps = [];
            matched.course.modules.forEach(m => {
                m.steps.forEach(s => allSteps.push(s));
            });

            if (!activeStep) {
                const nextActive = allSteps.find(s => {
                    const sp = matched.progress.stepsHistory.find(sh => sh.stepId === s._id);
                    return sp && sp.status === 'unlocked';
                }) || allSteps.find(s => {
                    const sp = matched.progress.stepsHistory.find(sh => sh.stepId === s._id);
                    return sp && sp.status === 'completed';
                }) || allSteps[0];
                
                setActiveStep(nextActive);
            } else {
                const updatedActive = allSteps.find(s => s._id === activeStep._id);
                if (updatedActive) setActiveStep(updatedActive);
            }
            
            setLoading(false);
        } catch (err) {
            console.error('Error fetching course:', err);
            setError('Помилка завантаження курсу.');
            setLoading(false);
        }
    };

    const handleCompleteTheory = async (stepId) => {
        try {
            setCompleting(true);
            await axios.post(`${API_URL}/courses/student/complete-step/${courseId}/${stepId}`);
            await fetchCourseProgress();

            const allSteps = [];
            course.modules.forEach(m => {
                m.steps.forEach(s => allSteps.push(s));
            });
            const curIdx = allSteps.findIndex(s => s._id === stepId);
            if (curIdx !== -1 && curIdx + 1 < allSteps.length) {
                setStepTransition('slide-left');
                setTimeout(() => {
                    setActiveStep(allSteps[curIdx + 1]);
                    setStepTransition('');
                }, 150);
            }
        } catch (err) {
            console.error('Error completing step:', err);
            alert('Не вдалося зберегти прогрес уроку.');
        } finally {
            setCompleting(false);
        }
    };

    const handleStartPractice = async (stepId) => {
        // Open a new tab immediately to bypass popup blockers
        const newWindow = window.open('', '_blank');
        if (newWindow) {
            newWindow.document.write('<div style="font-family: sans-serif; text-align: center; margin-top: 50px;">Завантаження тесту...</div>');
        }
        try {
            setTestLinkLoading(true);
            const res = await axios.post(`${API_URL}/courses/student/get-test-link/${courseId}/${stepId}`);
            const { hash, type } = res.data;
            
            let path = '';
            if (type === 'Quiz') path = `/quiz/${hash}`;
            else if (type === 'DeskTemplate') path = `/test/${hash}`;
            else if (type === 'MultiDeskTest') path = `/multi-test/${hash}`;
            else if (type === 'GameScenario') path = `/game/${hash}`;
            else if (type === 'PizzaTemplate') path = `/pizza/play/${hash}`;
            else if (type === 'ComplexTest') path = `/complex/${hash}`;
            
            if (path) {
                if (newWindow) {
                    newWindow.location.href = window.location.origin + path;
                } else {
                    window.location.href = path;
                }
            } else {
                if (newWindow) newWindow.close();
                alert('Непідтримуваний тип оцінювання.');
            }
        } catch (err) {
            if (newWindow) newWindow.close();
            console.error('Error fetching test link:', err);
            alert('Не вдалося згенерувати посилання для проходження тесту.');
        } finally {
            setTestLinkLoading(false);
        }
    };

    const handleStepClick = useCallback((step) => {
        setStepTransition('slide-left');
        setTimeout(() => {
            setActiveStep(step);
            setStepTransition('');
        }, 100);
        setIsSidebarOpen(false);
    }, []);

    const getYoutubeEmbedUrl = (url) => {
        if (!url) return '';
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        if (match && match[2].length === 11) {
            return `https://www.youtube.com/embed/${match[2]}`;
        }
        return url;
    };

    const isAllCompleted = course && progress
        ? (() => {
            const stepsHistoryMap = {};
            progress.stepsHistory.forEach(sh => { stepsHistoryMap[sh.stepId] = sh; });
            const allSteps = [];
            course.modules.forEach(m => m.steps.forEach(s => allSteps.push(s)));
            return allSteps.every(s => stepsHistoryMap[s._id]?.status === 'completed');
        })()
        : false;

    useEffect(() => {
        if (!isAllCompleted && prevAllCompletedRef.current) {
            setShowCompletion(true);
        }
        prevAllCompletedRef.current = isAllCompleted;
    }, [isAllCompleted]);

    useEffect(() => {
        setActiveAttachment(null);
    }, [activeStep]);

    if (loading) return <div className="placeholder-view">Завантаження плеєра навчання...</div>;
    if (error) return <div className="placeholder-view error-view">❌ {error} <button className="btn-ghost" onClick={() => navigate('/dashboard')}>На головну</button></div>;
    if (!course || !progress) return null;

    const stepsHistoryMap = {};
    progress.stepsHistory.forEach(sh => {
        stepsHistoryMap[sh.stepId] = sh;
    });

    const allSteps = [];
    let stepGlobalIndex = 0;
    course.modules.forEach(m => {
        m.steps.forEach(s => {
            allSteps.push({ ...s, globalIdx: stepGlobalIndex++ });
        });
    });

    const getModuleProgress = (module) => {
        const completed = module.steps.filter(s => stepsHistoryMap[s._id]?.status === 'completed').length;
        return { completed, total: module.steps.length };
    };

    const handleRefreshProgress = async () => {
        setLoading(true);
        await fetchCourseProgress();
    };

    return (
        <div className="course-player-container">
            {isAllCompleted && showCompletion ? (
                <div className="course-completed-screen">
                    <div className="completed-content">
                        <div className="completed-icon">🎉</div>
                        <h1>Вітаємо!</h1>
                        <p className="completed-subtitle">Ви успішно завершили курс</p>
                        <h2 className="completed-course-title">{course.title}</h2>
                        <div className="completed-stats">
                            <div className="completed-stat">
                                <span className="completed-stat-value">{allSteps.length}</span>
                                <span className="completed-stat-label">кроків пройдено</span>
                            </div>
                            <div className="completed-stat">
                                <span className="completed-stat-value">{course.modules.length}</span>
                                <span className="completed-stat-label">модулів вивчено</span>
                            </div>
                            <div className="completed-stat">
                                <span className="completed-stat-value">100%</span>
                                <span className="completed-stat-label">прогрес</span>
                            </div>
                        </div>
                        <div className="completed-actions">
                            <button className="btn-completed-primary" onClick={() => navigate('/dashboard')}>
                                ← На головну
                            </button>
                            <button className="btn-completed-secondary" onClick={() => {
                                setShowCompletion(false);
                                setActiveStep(allSteps[0]);
                            }}>
                                🔄 Переглянути ще раз
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <aside className={`course-player-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                        <div className="sidebar-header">
                            <button className="btn-back" onClick={() => navigate('/dashboard')}>
                                ← Всі курси
                            </button>
                            <h2>{course.title}</h2>
                            <div className="overall-progress-container">
                                <div className="progress-text">
                                    <span>Ваш прогрес:</span>
                                    <strong>{progress.percentage}%</strong>
                                </div>
                                <div className="progress-bar-wrapper">
                                    <div className="progress-bar-fill" style={{ width: `${progress.percentage}%` }} />
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-modules">
                            {course.modules.map((mod, mIdx) => {
                                const modProgress = getModuleProgress(mod);
                                const modDone = modProgress.completed === modProgress.total;
                                return (
                                    <div key={mod._id || mIdx} className="sidebar-module">
                                        <div className="sidebar-module-header">
                                            <h3>{mod.title}</h3>
                                            <div className="module-progress-info">
                                                {modDone ? (
                                                    <span className="module-done-badge">✅</span>
                                                ) : (
                                                    <span className="module-step-count">{modProgress.completed}/{modProgress.total}</span>
                                                )}
                                            </div>
                                        </div>
                                        {mod.description && <p className="module-desc">{mod.description}</p>}
                                        <div className="sidebar-steps">
                                            {mod.steps.map((step, sIdx) => {
                                                const stepProgress = stepsHistoryMap[step._id] || { status: 'locked' };
                                                const isLocked = stepProgress.status === 'locked';
                                                const isCompleted = stepProgress.status === 'completed';
                                                const isActive = activeStep?._id === step._id;
                                                const localStepNum = sIdx + 1;

                                                return (
                                                    <button
                                                        key={step._id || sIdx}
                                                        className={`sidebar-step-btn ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''} ${isCompleted ? 'completed' : ''}`}
                                                        disabled={isLocked}
                                                        onClick={() => handleStepClick(step)}
                                                    >
                                                        <span className="step-number">{localStepNum}</span>
                                                        <span className="step-icon">
                                                            {isCompleted ? '✅' : isLocked ? '🔒' : typeIcons[step.type]}
                                                        </span>
                                                        <div className="step-btn-info">
                                                            <span className="step-btn-title">{step.title}</span>
                                                            <span className="step-btn-type">{typeNames[step.type]}</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </aside>

                    <main className="course-player-content">
                        <button 
                            className={`mobile-sidebar-toggle ${isSidebarOpen ? 'open' : ''}`} 
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            aria-label="Toggle menu"
                        >
                            {isSidebarOpen ? '✕' : '☰'}
                        </button>
                        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
                        
                        {activeStep ? (
                            <div className={`active-step-workspace glass-panel ${stepTransition}`}>
                                <div className="workspace-header">
                                    <div className="workspace-header-top">
                                        <span className="step-badge">{typeNames[activeStep.type]}</span>
                                        <button className="btn-ghost btn-refresh" onClick={handleRefreshProgress} title="Оновити прогрес">
                                            🔄
                                        </button>
                                    </div>
                                    <h1>{activeStep.title}</h1>
                                </div>

                                <div className="workspace-body">
                                    {activeStep.type === 'text' && (
                                        <div 
                                            className="theory-text-content"
                                            dangerouslySetInnerHTML={{ __html: activeStep.contentHtml }}
                                        />
                                    )}

                                    {activeStep.type === 'video' && (
                                        <div className="theory-video-content">
                                            {activeStep.mediaUrl.includes('youtube.com') || activeStep.mediaUrl.includes('youtu.be') ? (
                                                <div className="video-iframe-wrapper">
                                                    <iframe
                                                        src={getYoutubeEmbedUrl(activeStep.mediaUrl)}
                                                        title={activeStep.title}
                                                        frameBorder="0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                </div>
                                            ) : (
                                                <video controls className="html5-video-player" key={resolveAssetUrl(activeStep.mediaUrl)}>
                                                    <source src={resolveAssetUrl(activeStep.mediaUrl)} type="video/mp4" />
                                                    Ваш браузер не підтримує тег відео.
                                                </video>
                                            )}
                                        </div>
                                    )}

                                    {activeStep.type === 'pdf' && (
                                        <div className="theory-pdf-content">
                                            {activeAttachment ? (
                                                <>
                                                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        <button 
                                                            className="btn-ghost" 
                                                            onClick={() => setActiveAttachment(null)}
                                                            style={{ alignSelf: 'flex-start' }}
                                                        >
                                                            ← Повернутися до матеріалів кроку
                                                        </button>
                                                        <h3 style={{ margin: '0.5rem 0 0 0', color: 'var(--text-main)', fontSize: '1.1rem', fontWeight: 700 }}>
                                                            📎 Документ: {activeAttachment.title}
                                                        </h3>
                                                    </div>
                                                    <PdfStep
                                                        url={activeAttachment.url}
                                                        title={activeAttachment.title}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <PdfStep
                                                        url={resolveAssetUrl(activeStep.mediaUrl)}
                                                        title={activeStep.title}
                                                    />
                                                    {activeStep.attachments && activeStep.attachments.length > 0 && (
                                                        <div className="step-attachments">
                                                            <h4 className="step-attachments-title">📎 Додаткові документи</h4>
                                                            <div className="step-attachments-list">
                                                                {activeStep.attachments.map((att, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => setActiveAttachment({
                                                                            url: resolveAssetUrl(att.url),
                                                                            title: att.title || 'Документ'
                                                                        })}
                                                                        className="step-attachment-link"
                                                                    >
                                                                        <span className="step-attachment-icon">📄</span>
                                                                        <span className="step-attachment-name">{att.title || 'Документ'}</span>
                                                                        <span className="step-attachment-action">Читати в цьому вікні →</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {activeStep.type === 'flashcards' && (
                                        <FlashcardStep
                                            contentHtml={activeStep.contentHtml}
                                            courseId={courseId}
                                            stepId={activeStep._id}
                                            onComplete={async () => {
                                                try {
                                                    await axios.post(`${API_URL}/courses/student/complete-step/${courseId}/${activeStep._id}`, {});
                                                    await fetchCourseProgress();
                                                } catch (err) {
                                                    console.error('Error saving flashcards completion:', err);
                                                }
                                            }}
                                        />
                                    )}

                                    {activeStep.type === 'hotspots' && (
                                        <HotspotStep
                                            mediaUrl={resolveAssetUrl(activeStep.mediaUrl)}
                                            contentHtml={activeStep.contentHtml}
                                            minScoreToPass={activeStep.minScoreToPass}
                                            onComplete={async (score) => {
                                                try {
                                                    await axios.post(`${API_URL}/courses/student/complete-step/${courseId}/${activeStep._id}`, { score });
                                                    await fetchCourseProgress();
                                                } catch (err) {
                                                    console.error('Error saving hotspots completion:', err);
                                                }
                                            }}
                                        />
                                    )}

                                    {activeStep.type === 'embed' && (
                                        <EmbedStep
                                            step={activeStep}
                                            courseId={courseId}
                                            onComplete={async () => {
                                                try {
                                                    await axios.post(`${API_URL}/courses/student/complete-step/${courseId}/${activeStep._id}`, {});
                                                    await fetchCourseProgress();
                                                } catch (err) {
                                                    console.error('Error saving embed completion:', err);
                                                }
                                            }}
                                        />
                                    )}

                                    {['Quiz', 'DeskTemplate', 'MultiDeskTest', 'GameScenario', 'PizzaTemplate', 'ComplexTest'].includes(activeStep.type) && (
                                        <div className="practice-step-content">
                                            <div className="practice-info-card">
                                                <div className="practice-icon">{typeIcons[activeStep.type]}</div>
                                                <h2>Завдання для перевірки знань</h2>
                                                <p>Пройти тест на прохідний бал не менше <strong>{activeStep.minScoreToPass}%</strong>.</p>
                                                
                                                {(() => {
                                                    const stepProgress = stepsHistoryMap[activeStep._id] || {};
                                                    const bestScore = stepProgress.score || 0;
                                                    const passed = stepProgress.status === 'completed';

                                                    return (
                                                        <div className="practice-stats">
                                                            <div className="stat-row">
                                                                <span>Найкращий результат:</span>
                                                                <strong className={passed ? 'score-pass' : 'score-fail'}>{bestScore}%</strong>
                                                            </div>
                                                            <div className="stat-row">
                                                                <span>Спроб:</span>
                                                                <strong>{stepProgress.attemptsCount || 0}</strong>
                                                            </div>
                                                            <div className="stat-row">
                                                                <span>Статус:</span>
                                                                <span className={`status-badge ${passed ? 'completed' : 'pending'}`}>
                                                                    {passed ? 'Виконано' : 'Не пройдено'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            <div className="practice-action-box">
                                                <button 
                                                    className="btn-practice-start" 
                                                    onClick={() => handleStartPractice(activeStep._id)}
                                                    disabled={testLinkLoading}
                                                >
                                                    {testLinkLoading ? 'Генерація...' : '🚀 Розпочати'}
                                                </button>
                                                <p className="practice-hint">Тест відкриється у новій вкладці. Після завершення натисніть 🔄 щоб оновити прогрес.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {['text', 'video', 'pdf', 'flashcards', 'embed'].includes(activeStep.type) && (
                                    <div className="workspace-footer">
                                        {stepsHistoryMap[activeStep._id]?.status === 'completed' ? (
                                            <div className="completed-lesson-banner">
                                                ✨ Урок вивчено! Можете переходити далі.
                                            </div>
                                        ) : (
                                            <button
                                                className="btn-complete-lesson"
                                                onClick={() => handleCompleteTheory(activeStep._id)}
                                                disabled={completing}
                                            >
                                                {completing ? 'Збереження...' : '📖 Позначити як вивчено'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-workspace glass-panel">
                                <div className="empty-workspace-icon">📚</div>
                                <h1>Оберіть урок</h1>
                                <p>Виберіть доступний урок у структурі курсу ліворуч, щоб розпочати навчання.</p>
                                <div className="empty-progress-hint">
                                    <span>Загальний прогрес:</span>
                                    <strong>{progress.percentage}%</strong>
                                </div>
                            </div>
                        )}
                    </main>
                </>
            )}
        </div>
    );
};

export default CoursePlayer;
